import { Link } from "react-router-dom";
import { NumericField } from "@/components/NumericField";
import { capacityRequestHref } from "@/lib/capacityRequest";
import {
  formatPremium,
  premiumFormula,
  PREMIUM_TERMS,
  PREMIUM_TO_INVOICE,
} from "@/lib/premium";
import {
  formatPriceChange,
  overLimitNotice,
  PRICE_CHANGE_NOTE,
  WITHIN_LIMITS,
  type CapacityPreview,
} from "@/lib/wonBid";

// BRK-S421 step 1, "Final price" — BRK-S184's first panel. The premium is
// DISPLAY ONLY (the served rate × the price being typed, BRK-S328), and the
// limit check is `wonCapacityPreview`'s: it names WHICH limit, never a figure
// of the program's, and never gates the finish (an over-limit win is a
// review upstream, not a refusal).

export function WonBidPriceStep({
  finalPrice,
  onPrice,
  error,
  change,
  showNote,
  premium,
  rate,
  preview,
  accountId,
}: {
  finalPrice: number | undefined;
  onPrice: (value: number | undefined) => void;
  error?: string;
  change: { amount: number; percent: number } | null;
  showNote: boolean;
  premium: number | null;
  rate: number | null;
  preview: CapacityPreview | null;
  accountId: string;
}) {
  return (
    <>
      <NumericField
        kind="currency"
        label="Final contract price"
        value={finalPrice}
        onChange={onPrice}
        required
        error={error}
      />
      {change !== null && (
        <p className="text-sm text-brk-dim" aria-live="polite">
          {formatPriceChange(change)}
        </p>
      )}
      {premium !== null && rate !== null && finalPrice !== undefined && (
        <div
          data-testid="premium-to-invoice"
          className="flex min-w-0 flex-col gap-0.5 rounded-md border border-brk-border p-3"
        >
          <p className="text-sm text-brk-dim">{PREMIUM_TO_INVOICE}</p>
          <p
            className="text-heading tabular-nums text-brk-text"
            aria-live="polite"
          >
            {formatPremium(premium)}
          </p>
          <p className="break-words text-sm text-brk-dim">
            {premiumFormula({
              amount: premium,
              contractPrice: finalPrice,
              ratePer1000: rate,
              basis: "final_contract_price",
            })}
          </p>
          <p className="text-sm text-brk-dim">{PREMIUM_TERMS}</p>
        </div>
      )}
      {showNote && (
        // INFORMATIONAL — neutral styling, never a warning colour.
        <p
          role="note"
          className="rounded-md border border-brk-border bg-brk-bg p-3 text-sm text-brk-text"
        >
          {PRICE_CHANGE_NOTE}
        </p>
      )}
      {preview === "within" && (
        <p
          data-testid="capacity-indicator"
          className="flex items-center gap-2 text-sm text-brk-text"
        >
          <svg
            aria-hidden="true"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0 text-brk-dim"
          >
            <path d="m5 12 5 5 9-11" />
          </svg>
          {WITHIN_LIMITS}
        </p>
      )}
      {(preview === "single" || preview === "aggregate") && (
        <div
          role="note"
          data-testid="capacity-indicator"
          className="flex flex-col gap-3 rounded-md border border-brk-border bg-brk-bg p-3 text-sm text-brk-text"
        >
          <p>{overLimitNotice(preview)}</p>
          {/* BRK-S329: information, never a gate — the won bid can still be
              finished first. */}
          <Link
            to={capacityRequestHref(accountId)}
            className="self-start text-sm font-medium text-brk-accent hover:underline"
          >
            Request more capacity
          </Link>
          <p className="text-brk-dim">
            You don’t have to wait — you can submit this won bid now.
          </p>
        </div>
      )}
    </>
  );
}
